import { LocalStorageCacheService } from '@/core/services/LocalStorageCacheService';
import { debugLogger } from '@/utils/debugLogger';

export interface CachedGeoLocation {
  latitude: number;
  longitude: number;
  city: string;
  state: string;
  stateCode: string;
  country: string;
  countryCode: string;
  timestamp: number;
}

/**
 * Gerencia cache persistente de dados de geolocalização
 * Cache tem TTL de 1 hora para respeitar rate limits da Google API
 */
export class GeoLocationCache {
  private static readonly CACHE_KEY = 'geo_location_cache';
  private static readonly CACHE_VERSION = '1.0';
  private static readonly TTL_SECONDS = 3600; // 1 hora

  /**
   * Salva dados de geolocalização no cache
   */
  static save(location: Omit<CachedGeoLocation, 'timestamp'>): boolean {
    try {
      const cacheService = LocalStorageCacheService.getInstance();
      const dataToCache: CachedGeoLocation = {
        ...location,
        timestamp: Date.now()
      };

      const success = cacheService.set(
        this.CACHE_KEY,
        dataToCache,
        {
          version: this.CACHE_VERSION,
          expiresIn: this.TTL_SECONDS
        }
      );

      if (success) {
        debugLogger.debug('[GeoLocationCache] Location saved to cache:', {
          city: location.city,
          state: location.stateCode,
          country: location.countryCode
        });
      }

      return success;
    } catch (error) {
      debugLogger.error('[GeoLocationCache] Failed to save location:', error);
      return false;
    }
  }

  /**
   * Recupera dados de geolocalização do cache
   * @param allowExpired Se true, retorna cache mesmo se expirado (fallback de emergência)
   * @returns Dados do cache ou null se não existir
   */
  static get(allowExpired: boolean = false): CachedGeoLocation | null {
    try {
      const cacheService = LocalStorageCacheService.getInstance();
      const cached = cacheService.get(this.CACHE_KEY);

      if (!cached) {
        debugLogger.debug('[GeoLocationCache] No cached location found');
        return null;
      }

      // Se permite expirado, retorna direto (para emergências)
      if (allowExpired) {
        const ageMinutes = Math.round((Date.now() - cached.timestamp) / 60000);
        debugLogger.debug('[GeoLocationCache] Returning expired cache as fallback:', {
          city: cached.city,
          state: cached.stateCode,
          ageMinutes
        });
        return cached as CachedGeoLocation;
      }

      // Verificar se ainda é válido
      if (!cacheService.isValid(this.CACHE_KEY)) {
        debugLogger.debug('[GeoLocationCache] Cached location expired (keeping for emergency fallback)');
        // ✅ NÃO deletar! Manter para situações de rate limit
        return null;
      }

      debugLogger.debug('[GeoLocationCache] Location retrieved from cache:', {
        city: cached.city,
        state: cached.stateCode,
        ageMinutes: Math.round((Date.now() - cached.timestamp) / 60000)
      });

      return cached as CachedGeoLocation;
    } catch (error) {
      debugLogger.error('[GeoLocationCache] Failed to get location:', error);
      return null;
    }
  }

  /**
   * Verifica se o cache é válido (não expirado)
   */
  static isValid(): boolean {
    const cacheService = LocalStorageCacheService.getInstance();
    return cacheService.isValid(this.CACHE_KEY);
  }

  /**
   * Remove dados do cache
   */
  static clear(): void {
    try {
      const cacheService = LocalStorageCacheService.getInstance();
      cacheService.remove(this.CACHE_KEY);
      debugLogger.debug('[GeoLocationCache] Cache cleared');
    } catch (error) {
      debugLogger.error('[GeoLocationCache] Failed to clear cache:', error);
    }
  }

  /**
   * Retorna idade do cache em minutos
   * Usa allowExpired=true para poder retornar idade mesmo de cache expirado
   */
  static getCacheAge(): number | null {
    const cached = this.get(true); // allowExpired para obter idade mesmo expirado
    if (!cached) return null;
    return Math.round((Date.now() - cached.timestamp) / 60000);
  }
}
